/* Member/trader governance state and mutations for the Manager org-settings
 * plane (#342). Owns the member list, the per-trader usage rollup, the org bot
 * gate, the role predicates, and every governance write.
 *
 * Moved out of TradersPanel in index.tsx at 859565e3 with behavior preserved
 * exactly — including the deliberate asymmetries called out in the comments
 * below. Deliberately imports nothing from the export side of this directory. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, useAuthStore } from '../../stores/authStore';
import type { TraderRules } from '../../types/shared/admin';
import { parseTraderPrefs } from '../../lib/traderPrefs';
import type { OrgMember, MemberUsage } from './memberTypes';

interface PositionsByTraderRow {
  userId: string;
  openCount: number;
  openPositionsCount?: number;
  intentContracts?: number;
  maxContracts: number | null;
  maxPositions: number | null;
  traderRules?: TraderRules | null;
}

export interface UseMemberGovernanceArgs {
  adminMode: boolean | undefined;
  /** '' outside adminMode, exactly as TradersPanel computes it. */
  selectedOrgId: string;
  orgQueryParam: string;
  selectedOrg: { id: string; name: string } | null;
  adminOrgsLoaded: boolean;
}

export interface MemberGovernance {
  members: OrgMember[];
  usageByUserId: Record<string, MemberUsage>;
  /** #444 — admin mode with no organization chosen: a resolved, non-requesting
   *  scope, NOT a loading one.
   *
   *  A consumer MUST branch on this before rendering `members`, which still
   *  holds the previously selected organization's cards and is deliberately not
   *  cleared (see the note by the derivation below). Ordering against `loading`
   *  is free — `loading` is already false in this scope — so the two guards are
   *  mutually redundant on the spinner and only jointly load-bearing. Checking
   *  this first anyway keeps the intent legible. */
  awaitingOrg: boolean;
  loading: boolean;
  /** The member list read for the current org failed — distinct from an org
   *  with no members. Null when the read succeeded or is in flight. */
  membersLoadError: string | null;
  /** Re-reads the member list for the current org. */
  reloadMembers: () => Promise<MembersRead>;
  error: string | null;
  /** Shared with the Google Sheets handlers still living in index.tsx. */
  setError: (e: string | null) => void;
  generatedPassword: string | null;
  clearGeneratedPassword: () => void;
  submitting: boolean;
  managerSubmitting: boolean;
  orgBotsEnabled: boolean | null;
  /** PERMANENTLY stable identity (#349) — OrgBotDefaultsPanel keeps
   *  `onEnabledChange` in an effect dep array, so this must never be recreated:
   *  not per render, and not on an org switch either. It is no longer the raw
   *  setState — the value it writes is stamped with the org being read — but it
   *  reads that org from a ref precisely so the identity does not move. Keying
   *  it on the org instead re-fires the panel's push on the switch commit, where
   *  the panel still holds the OUTGOING org's value. */
  setOrgBotsEnabled: (v: boolean | null) => void;
  canCreateManager: boolean;
  /** #460 — may a trader be created in the CURRENT scope?
   *
   *  Deliberately NOT `canCreateManager` with the role term dropped. Manager
   *  creation is admin-only; trader creation is not, and a non-admin manager's
   *  `selectedOrg` is ALWAYS null by construction — index.tsx builds the list as
   *  `adminMode ? storeOrgs : []` — so reusing that predicate here would disable
   *  the control for every manager in the product. Only the admin plane, where
   *  an organization is actually chosen, can be unresolved.
   *
   *  Stricter than `awaitingOrg`, which only covers the EMPTY selection. A
   *  stale id is non-empty and so passes that test while still naming no
   *  organization, which is why this resolves against the loaded list (#295)
   *  rather than trusting `selectedOrgId`. */
  canCreateTrader: boolean;
  canGovern: (m: OrgMember) => boolean;
  createTrader: (username: string, onCreated: () => void) => Promise<void>;
  createManager: (username: string, onCreated: () => void) => Promise<void>;
  /** true = stop editing (saved, or the member vanished); false = keep editing
   *  because a server error is now on screen. */
  renameMember: (id: string, username: string) => Promise<boolean>;
  deleteMember: (m: OrgMember) => Promise<void>;
  resetPassword: (m: OrgMember) => Promise<void>;
  toggleUseOrgKeys: (id: string, next: boolean) => Promise<void>;
  toggleBotsEnabled: (id: string, next: boolean) => Promise<void>;
  toggleOscarEnabled: (id: string, next: boolean) => Promise<void>;
  toggleOneClickEnabled: (id: string, next: boolean) => Promise<void>;
  toggleLt2Enabled: (id: string, next: boolean) => Promise<void>;
  toggleCockpitEnabled: (id: string, next: boolean) => Promise<void>;
  toggleRfqEnabled: (id: string, next: boolean) => Promise<void>;
  /** TapeLab level (traderRules.labAccess). 'off' removes the key — the relay
   *  rejects null/false, and an absent key is what fails closed. */
  setLabAccess: (id: string, next: 'off' | 'viewer' | 'researcher') => Promise<void>;
  toggleLiveGameEnabled: (id: string, next: boolean) => Promise<void>;
  toggleSniperEnabled: (id: string, next: boolean) => Promise<void>;
  togglePenelopeEnabled: (id: string, next: boolean) => Promise<void>;
  togglePenelopeLiveEnabled: (id: string, next: boolean) => Promise<void>;
  togglePenelopeAdvancedEnabled: (id: string, next: boolean) => Promise<void>;
  toggleAttackEnabled: (id: string, next: boolean) => Promise<void>;
  /** Low bandwidth mode (users.prefs.lowBandwidth) — a trader preference, not
   *  a grant; merged into the stored prefs by the relay. */
  setLowBandwidth: (id: string, next: boolean) => Promise<void>;
  /** EditLimitsModal `onSaved` merge. The parameter is the NARROW shape on
   *  purpose: EditLimitsModal types `onSaved` against its own non-exported
   *  TraderSummary, and under `strict` an `(next: OrgMember) => void` is not
   *  assignable there. */
  applyMemberPatch: (
    next: Pick<OrgMember, 'id' | 'username' | 'maxContracts' | 'maxPositions' | 'traderRules'>,
  ) => void;
}

type MembersRead = 'ok' | 'failed' | 'superseded';

/** The traderRules flags the member-card chips own. (streamrift: the lane's
 *  extra chips — Cockpit, RFQ, Live Game, Sniper, Penelope's three locks —
 *  write through the same queue; main df2d8649c covered its four.) */
type RuleFlag =
  | 'oscarEnabled' | 'oneClickEnabled' | 'lt2Enabled' | 'attackEnabled'
  | 'cockpitEnabled' | 'rfqEnabled' | 'liveGameEnabled' | 'sniperEnabled'
  | 'penelopeEnabled' | 'penelopeLiveEnabled' | 'penelopeAdvancedEnabled';
const RULE_NAME: Record<RuleFlag, string> = {
  oscarEnabled: 'Oscar',
  oneClickEnabled: '1-click',
  lt2Enabled: 'Live Trading V2',
  attackEnabled: 'Attack',
  cockpitEnabled: 'Cockpit',
  rfqEnabled: 'RFQ',
  liveGameEnabled: 'Live Game',
  sniperEnabled: 'Sniper',
  penelopeEnabled: 'Penelope',
  penelopeLiveEnabled: 'Penelope live',
  penelopeAdvancedEnabled: 'Penelope advanced',
};
/** Penelope's locks are OPT-OUT (2026-10-08): only the literal false locks, and
 *  "on" is spelled by DROPPING the key (the relay resolves absent as on). A
 *  revoked lock that reads back absent is therefore ON again, not unknown. */
const OPT_OUT_FLAGS: ReadonlySet<RuleFlag> = new Set<RuleFlag>(['penelopeEnabled', 'penelopeLiveEnabled', 'penelopeAdvancedEnabled']);
const flagOn = (rules: TraderRules | null, flag: RuleFlag): boolean =>
  OPT_OUT_FLAGS.has(flag) ? rules?.[flag] !== false : rules?.[flag] === true;
/** A null-rules member's unrestricted-equivalent baseline (parseTraderRulesField
 *  requires all three base fields). */
const BASELINE_RULES: TraderRules = { allowedSports: null, nonSportsEnabled: true, polymarketEnabled: true };

/** The reply body, or {} when there is none or it is not JSON (a proxy's 502
 *  page): a failed write is still reported, never thrown away. */
async function replyBody(res: Response): Promise<{ user?: Partial<OrgMember>; error?: unknown }> {
  try {
    const body: unknown = await res.json();
    return body && typeof body === 'object' ? body as { user?: Partial<OrgMember>; error?: unknown } : {};
  } catch {
    return {};
  }
}

export function useMemberGovernance({
  adminMode,
  selectedOrgId,
  orgQueryParam,
  selectedOrg,
  adminOrgsLoaded,
}: UseMemberGovernanceArgs): MemberGovernance {
  const currentUserId = useAuthStore((s) => s.user?.id) ?? null;
  const currentUserRole = useAuthStore((s) => s.user?.role) ?? null;

  const [members, setMembers] = useState<OrgMember[]>([]);
  const [usageByUserId, setUsageByUserId] = useState<Record<string, MemberUsage>>({});
  // #349 — `loading` is DERIVED from which org the list has been read for, not
  // stored. There is nothing to set true when a read starts: an org the list has
  // not been read for is, by definition, still loading. Deliberately not keyed
  // off `members` — the governance writes update that array ten different ways
  // and restamping every one of them is both out of scope and the riskiest code
  // in this file.
  const [loadedOrg, setLoadedOrg] = useState<string | null>(null);
  const [membersLoadErrorFor, setMembersLoadErrorFor] = useState<{ org: string; value: string } | null>(null);
  // Only the latest list read may write: an org switch (or a re-read from
  // createManager) makes every earlier reply stale, whichever lands last.
  const membersReadSeq = useRef(0);
  // The org `members` currently holds a successful read for. A failed re-read
  // of that same org keeps its list (a stale list, reported by the caller); a
  // failed first read of an org shows the load error instead of "No members".
  const membersOrgRef = useRef<string | null>(null);
  // #444 — an admin who has chosen no organization is not loading. Both effects
  // below return without issuing a request on exactly this condition, so there
  // is nothing in flight for a spinner to represent; before this, the scope was
  // simply never marked resolved and the panel spun forever.
  //
  // Not a new concept: `orgKeysVisible` (index.tsx) is this same predicate
  // inverted, and already hides the org keys, rebate, bot-defaults and trigger
  // panels in this scope — which is why none of them issue a request either.
  // The member grid was the one panel that never got it.
  //
  // Derived, not stored, and deliberately NOT paired with clearing `members`:
  // that array also feeds useExportWorkflows (index.tsx), so blanking it here
  // would reach into the export panels' trader list — outside this issue. The
  // stale cards are kept out of the UI by branching on `awaitingOrg` first,
  // which is why the interface note above makes that ordering a contract.
  const awaitingOrg = !!adminMode && !selectedOrgId;
  const loading = !awaitingOrg && loadedOrg !== orgQueryParam;
  const membersLoadError = !loading && membersLoadErrorFor?.org === orgQueryParam ? membersLoadErrorFor.value : null;
  const [error, setError] = useState<string | null>(null);
  // Rules-chip safety. The relay replaces traderRules wholesale, so every chip
  // write is built from the latest rules the relay confirmed for that member
  // (not from a render's `members`, which lags a reply still in flight), and
  // one member's rules writes run one at a time. `revokedRules` remembers which
  // capabilities this manager turned off, so a later reply showing one back ON
  // is called out. Since #1205 the relay refuses a current editor's write built
  // from older rules (409, treated as a fresh read below), so a call-out comes
  // from an older client or script without a starting point re-sending it — or
  // from rules picked up by a rename / org-keys / bots reply, which update the
  // card but not these maps (pre-existing).
  const confirmedRules = useRef(new Map<string, TraderRules | null>());
  const rulesWrites = useRef(new Map<string, Promise<void>>());
  const revokedRules = useRef(new Map<string, Set<RuleFlag>>());
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [managerSubmitting, setManagerSubmitting] = useState(false);
  // Org-level bot switch — gates the per-trader bot toggle (effective bot access
  // = org.botsEnabled AND user.botsEnabled, so granting a trader is moot while
  // the org switch is off). null = not yet known.
  //
  // #349 — STAMPED with the org it describes, and derived by matching that stamp
  // against the current org. An org change therefore returns the gate to
  // "unknown" by derivation instead of by a reset scheduled inside the effect,
  // so a previous org's answer can never gate the new org's trader chips.
  const [orgBotsFor, setOrgBotsFor] = useState<{ org: string; value: boolean | null } | null>(
    null,
  );
  const orgBotsEnabled = orgBotsFor?.org === orgQueryParam ? orgBotsFor.value : null;
  // The org this hook is currently reading for, held in a ref so the setter
  // below can stamp with it WITHOUT taking it as a dependency. Updated in an
  // effect rather than during render — a render-phase ref write trips
  // `react-hooks/refs` (#148).
  const orgRef = useRef(orgQueryParam);
  useEffect(() => {
    orgRef.current = orgQueryParam;
  }, [orgQueryParam]);

  // PERMANENTLY stable identity — see the interface note on `setOrgBotsEnabled`.
  // OrgBotDefaultsPanel holds this in an effect dep array, so the identity must
  // not move: not per render, and not on an org switch either. Keying it on the
  // org (the first #349 attempt) re-fired that effect on the switch commit,
  // where the panel still closes over the PREVIOUS org's `enabled` — which
  // re-stamped org 1's answer onto org 2 and declared org 2's trader grants
  // inert before anything was known about them. Pinned by
  // managerOrgSwitchGate.test.tsx, which mounts the real panel.
  //
  // Ordering that makes this correct: child effects run before the parent's, so
  // a push landing on the switch commit stamps with the OUTGOING org and is
  // derived away immediately. By the time the panel's own fetch resolves, the
  // effect above has moved the ref on.
  const setOrgBotsEnabled = useCallback((v: boolean | null) => {
    setOrgBotsFor({ org: orgRef.current, value: v });
  }, []);

  // Manager creation is admin-only, and that is enforced by the RELAY:
  // /api/admin/* mounts requireRole('admin') on '*' (relay/src/routes/admin.ts).
  // This gate only avoids showing a control that would 403 — it does not grant
  // anything, and a manager who forged the call still gets refused server-side.
  const canCreateManager = currentUserRole === 'admin' && adminOrgsLoaded && selectedOrg !== null;

  // #460 — gates on the admin PLANE, not on the caller's role: a manager
  // creating a trader is scoped by the server and has no organization to
  // choose, so `!adminMode` is the whole answer for them. See the interface
  // note for why this is not `canCreateManager` minus the role check.
  const canCreateTrader = !adminMode || (adminOrgsLoaded && selectedOrg !== null);

  /** #295 — may the caller govern this member's ACCOUNT (rename / reset password
   *  / delete) from this plane?
   *
   *  Manager rows only, admin callers only. Admin rows are deliberately excluded:
   *  admins are box-scoped and their governance stays in Administration. Trader
   *  rows are unaffected — they keep the manager-or-admin path they already had.
   *  Mirrors the relay, which mounts requireRole('admin') on all of /api/admin/*. */
  const canGovern = (m: OrgMember) => currentUserRole === 'admin' && m.role === 'manager';

  // Load the org-level bot switch independently of the settings panel (which may
  // not be mounted in the current view) so the per-trader bot toggle can gate on
  // it. OrgBotDefaultsPanel also reports changes up via onEnabledChange, so a
  // toggle there greys/ungreys the trader chips live.
  useEffect(() => {
    // No reset needed on either arm: nothing is stamped for the new org yet, so
    // `orgBotsEnabled` already derives to null.
    if (adminMode && !selectedOrgId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/manager/org${orgQueryParam}`);
        const data = await res.json();
        // A failed read leaves the gate unknown (null = don't gate), never "org off".
        if (!cancelled) setOrgBotsEnabled(res.ok ? !!data.org?.botsEnabled : null);
      } catch {
        if (!cancelled) setOrgBotsEnabled(null);
      }
    })();
    return () => { cancelled = true; };
  }, [orgQueryParam, adminMode, selectedOrgId, setOrgBotsEnabled]);

  /** `ok` when the list for the current org was read (or there is no org to
   *  read); `failed` when the read failed; `superseded` when a newer read (an
   *  org switch, a re-read) made this one irrelevant — not a failure. */
  const fetchMembers = useCallback(async (): Promise<MembersRead> => {
    // Nothing to read for an admin with no org chosen: mark this scope resolved
    // so the grid renders its empty state rather than spinning. (Unreachable
    // from the effect below, which returns on the same condition — kept because
    // createManager also calls through here.)
    if (adminMode && !selectedOrgId) { setLoadedOrg(orgQueryParam); return 'ok'; }
    // Two parallel fetches: identity list (limits live on the user row),
    // and the per-trader rollup that gives us live usage for the fill bars.
    // Rollup is best-effort — if it fails we still render the cards without
    // bars rather than blank the whole grid.
    const seq = ++membersReadSeq.current;
    const org = orgQueryParam;
    const fail = (msg: string): MembersRead => {
      if (seq !== membersReadSeq.current) return 'superseded';
      if (membersOrgRef.current === org) return 'failed';
      // The list is wiped, so it no longer holds any org's read: a later
      // failure for the org it used to hold must show the error, not spin.
      membersOrgRef.current = null;
      setMembers([]);
      setUsageByUserId({});
      setMembersLoadErrorFor({ org, value: msg });
      setLoadedOrg(org);
      return 'failed';
    };
    let tradersRes: Response;
    let usageRes: Response | null;
    let data: { traders?: OrgMember[]; error?: string };
    try {
      [tradersRes, usageRes] = await Promise.all([
        apiFetch(`/api/manager/traders${orgQueryParam}`),
        apiFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`).catch(() => null),
      ]);
      data = await tradersRes.json();
    } catch {
      return fail('Network error');
    }
    if (seq !== membersReadSeq.current) return 'superseded';
    if (!tradersRes.ok) return fail(data.error ?? 'Failed to load members');
    membersOrgRef.current = org;
    setMembersLoadErrorFor(null);
    setMembers(data.traders ?? []);
    // A fresh read is what the cards now show: build on it, and stop calling
    // out capabilities it already shows ON.
    for (const m of data.traders ?? []) {
      confirmedRules.current.set(m.id, m.traderRules ?? null);
      revokedRules.current.delete(m.id);
    }
    if (usageRes && usageRes.ok) {
      const usageData = (await usageRes.json().catch(() => ({}))) as {
        rows?: PositionsByTraderRow[];
      };
      if (seq !== membersReadSeq.current) return 'superseded';
      const map: Record<string, MemberUsage> = {};
      for (const r of usageData.rows ?? []) {
        map[r.userId] = {
          openContracts: r.openCount ?? 0,
          openPositions: r.openPositionsCount ?? 0,
          intentContracts: r.intentContracts ?? 0,
        };
      }
      setUsageByUserId(map);
    } else {
      setUsageByUserId({});
    }
    setLoadedOrg(orgQueryParam);
    return 'ok';
  }, [orgQueryParam, adminMode, selectedOrgId]);

  useEffect(() => {
    if (adminMode && !selectedOrgId) return;
    // #349 — suppressed with the substance of the diagnostic already fixed, not
    // instead of fixing it. The cascading render the rule exists to catch was
    // `setLoading(true)` firing on every read; `loading` is now derived from
    // `loadedOrg` and that call is gone. What the rule still sees is a static
    // trace into `fetchMembers`'s defensive no-org guard, which sets
    // `loadedOrg` synchronously — and which cannot fire on this path, because
    // the line above returns on exactly that condition. Inlining the fetch here
    // would satisfy the rule but break `createManager`, which re-reads the list
    // through this same callback (#295, pinned by orgSettingsManagers.test.tsx).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchMembers();
  }, [fetchMembers, adminMode, selectedOrgId]);

  /** Caller owns the empty-username and org-selected guards, and closes its own
   *  form from `onCreated` at the exact point the inline handler used to.
   *  No try/catch, deliberately: a network throw here strands `submitting`
   *  true, and createManager's `finally` is the documented asymmetry (#295). */
  const createTrader = async (username: string, onCreated: () => void) => {
    setSubmitting(true); setError(null); setGeneratedPassword(null);
    const res = await apiFetch(`/api/manager/traders${orgQueryParam}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); setSubmitting(false); return; }
    setMembers((prev) => [...prev, data.user]);
    setGeneratedPassword(data.generatedPassword);
    onCreated(); setSubmitting(false);
  };

  /** #295 — create a Manager in the CURRENTLY SELECTED org.
   *
   *  Uses the existing admin manager contract unchanged (POST /api/admin/managers).
   *  Two things differ from the trader path above and both matter:
   *   - the org travels in the BODY, not `?orgId=`. The admin router has no
   *     resolveOrgId, so `orgQueryParam` would be silently ignored here and the
   *     server would reject the call for a missing orgId.
   *   - the target is `selectedOrg.id`, the RESOLVED org, so a stale localStorage
   *     id can never become the org a manager is created in.
   *  Unlike createTrader it also unwinds `submitting` in a finally: a network
   *  throw there strands the button in its spinner forever. */
  const createManager = async (username: string, onCreated: () => void) => {
    if (!selectedOrg) return;
    setManagerSubmitting(true); setError(null); setGeneratedPassword(null);
    let created = false;
    try {
      const res = await apiFetch('/api/admin/managers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), orgId: selectedOrg.id }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(data?.error ?? `Could not create manager (${res.status}).`);
        return;
      }
      // Re-fetch rather than pushing `data.user` onto `members`: the admin
      // endpoint returns only {id, username, role, orgId, createdAt}, so an
      // optimistic push would seat a card whose limits/rules fields simply do
      // not exist. Re-reading gives the new manager the same shape every other
      // card is built from.
      setGeneratedPassword(data.generatedPassword);
      onCreated();
      created = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach the server.');
    } finally {
      setManagerSubmitting(false);
    }
    // Deliberately OUTSIDE the try above. By this point the manager exists on
    // the server, so a failed re-read is a stale LIST, not a failed creation —
    // reporting it as "could not create" would tell the admin the opposite of
    // what happened and invite them to create a second account.
    if (created) {
      if ((await fetchMembers()) === 'failed') {
        setError('Manager created, but the member list could not be refreshed — reload to see it.');
      }
    }
  };

  /** #295 — the ACCOUNT endpoints for a member (rename / reset password /
   *  delete), chosen by ROLE.
   *
   *  A manager row must never be sent to /api/manager/traders/:id for these
   *  three: each resolves through `manageableTraderFilter`, which pins
   *  role='trader' (relay/src/routes/manager.ts), so the call returns 404
   *  "Trader not found in this org" — a wrong-endpoint bug wearing a
   *  missing-member costume. Managers use the existing admin contract, which is
   *  unchanged and still admin-gated server-side.
   *
   *  NOT a blanket rule about the trader routes, and deliberately not used by
   *  the capability toggles below: that same PATCH widens to `readableMemberFilter`
   *  (any role, self included) when an ADMIN sends a traderRules-ONLY body, which
   *  is how a manager or admin row gets its oscar / one-click grant. Those
   *  toggles must stay on the trader route. Only these three — whose bodies are
   *  never rules-only — need the admin contract. */
  const memberAccountPath = (m: OrgMember, suffix = '') =>
    m.role === 'manager'
      ? `/api/admin/managers/${m.id}${suffix}`
      : `/api/manager/traders/${m.id}${suffix}${orgQueryParam}`;

  const deleteMember = async (target: OrgMember) => {
    const res = await apiFetch(memberAccountPath(target), { method: 'DELETE' });
    if (res.ok) {
      setMembers((prev) => prev.filter((t) => t.id !== target.id));
    } else { const data = await res.json(); setError(data.error); }
  };

  const resetPassword = async (target: OrgMember) => {
    const res = await apiFetch(memberAccountPath(target, '/reset-password'), { method: 'POST' });
    const data = await res.json();
    if (res.ok) setGeneratedPassword(data.generatedPassword);
    else setError(data.error);
  };

  const renameMember = async (id: string, username: string): Promise<boolean> => {
    const target = members.find((m) => m.id === id);
    // Defensive only, and deliberately untested: no UI path reaches it, because
    // a member dropping out of `members` unmounts their card and takes the Save
    // button with it. The lookup is new here (the old code sent `id` straight to
    // the trader route), so this states what happens rather than returning bare.
    if (!target) {
      setError('That member is no longer in this organization.');
      return true;
    }
    const res = await apiFetch(memberAccountPath(target), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      // Username only. The admin manager PATCH also accepts `orgId`, and #295
      // deliberately does NOT surface it: this plane is scoped to one org, so
      // moving a member out of it would make their card vanish from the list
      // being edited. Traders cannot change org at all, so surfacing it only for
      // managers would make this card MORE capable than a trader's. Org changes
      // also remain unfinished safety territory: #287's wind-down classification
      // landed in #297 but is explicitly NOT consumed in production yet.
      body: JSON.stringify({ username: username.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return false; }
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...data.user } : m)));
    return true;
  };

  const toggleUseOrgKeys = async (id: string, next: boolean) => {
    setError(null);
    const isSelf = id === currentUserId;
    const url = isSelf
      ? '/api/manager/me'
      : `/api/manager/traders/${id}${orgQueryParam}`;
    const res = await apiFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ useOrgKeys: next }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return; }
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...data.user } : m)));
  };

  // Per-trader bot access (AND-gated with the org's botsEnabled). Trader rows
  // only — admins/managers don't use the trader bot flow.
  const toggleBotsEnabled = async (id: string, next: boolean) => {
    setError(null);
    const res = await apiFetch(`/api/manager/traders/${id}${orgQueryParam}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ botsEnabled: next }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return; }
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...data.user } : m)));
  };

  /** After a confirmed rules reply: name any capability this manager turned off
   *  that the reply shows ON again. */
  const callOutResent = (id: string, username: string, rules: TraderRules | null) => {
    for (const flag of revokedRules.current.get(id) ?? []) {
      if (flagOn(rules, flag)) {
        const msg = `${RULE_NAME[flag]} is still ON for ${username}: another change re-sent it. Turn it off again.`;
        setError((prev) => (prev ? `${prev} ${msg}` : msg));
      }
    }
  };

  /** Every traderRules chip writes through here: one write per member at a
   *  time, each built from the latest confirmed rules, and every failure (HTTP,
   *  a non-JSON reply, the network) reported with what is still in force.
   *  The previous banner clears when the chip is pressed, not when its write
   *  runs, so a queued write never hides an earlier write's failure. */
  const writeRules = (
    id: string,
    what: string,
    still: string,
    build: (base: TraderRules) => TraderRules,
    revoke?: { flag: RuleFlag; next: boolean },
  ): Promise<void> => {
    setError(null);
    const username = members.find((m) => m.id === id)?.username ?? 'this member';
    const fallback = members.find((m) => m.id === id)?.traderRules ?? null;
    const report = (msg: string) => setError((prev) => (prev ? `${prev} ${msg}` : msg));
    const write = async () => {
      const known = confirmedRules.current;
      const base = (known.has(id) ? known.get(id) : fallback) ?? null;
      const sent: TraderRules = build(base ?? BASELINE_RULES);
      let res: Response;
      try {
        res = await apiFetch(`/api/manager/traders/${id}${orgQueryParam}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          // #1205: the rules this write started from; the relay refuses it
          // (409) if another editor changed them since.
          body: JSON.stringify({ traderRules: sent, expectedTraderRules: base }),
        });
      } catch {
        report(`Couldn't ${what} for ${username} (network error). ${still}`);
        return;
      }
      const data = await replyBody(res);
      if (res.status === 409 && (data as { code?: unknown }).code === 'TRADER_RULES_CHANGED') {
        // Another editor changed this member's rules. Show what is stored now
        // and let the manager decide again, rather than retrying blind.
        const latest = ((data as { traderRules?: TraderRules | null }).traderRules ?? null);
        known.set(id, latest);
        setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, traderRules: latest } : m)));
        report(`Didn't ${what} for ${username}: someone else changed their rules first. The card now shows the latest; press it again if you still want the change.`);
        // The refusal carries the stored rules — a fresh read for this member,
        // as fetchMembers is — so stop calling out capabilities it shows ON.
        revokedRules.current.delete(id);
        return;
      }
      if (!res.ok) {
        const reason = typeof data.error === 'string' && data.error ? `: ${data.error}` : '';
        report(`Couldn't ${what} for ${username} (HTTP ${res.status}${reason}). ${still}`);
        return;
      }
      const user = data.user && typeof data.user === 'object' ? data.user : {};
      // The relay stores exactly what it was sent; its echo wins when present.
      const rules = 'traderRules' in user ? user.traderRules ?? null : sent;
      known.set(id, rules);
      if (revoke) {
        const revoked = revokedRules.current.get(id) ?? new Set<RuleFlag>();
        if (revoke.next) revoked.delete(revoke.flag); else revoked.add(revoke.flag);
        revokedRules.current.set(id, revoked);
      }
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...user, traderRules: rules } : m)));
      callOutResent(id, username, rules);
    };
    const previous = rulesWrites.current.get(id) ?? Promise.resolve();
    const queued = previous.then(write, write);
    rulesWrites.current.set(id, queued);
    void queued.then(() => { if (rulesWrites.current.get(id) === queued) rulesWrites.current.delete(id); });
    return queued;
  };
  /** One boolean chip. An opt-out flag turned ON drops its key (absent = on). */
  const setRuleFlag = (id: string, flag: RuleFlag, next: boolean): Promise<void> =>
    writeRules(
      id,
      `turn ${RULE_NAME[flag]} ${next ? 'on' : 'off'}`,
      `It is still ${next ? 'OFF' : 'ON'}.`,
      (base) => {
        const sent: TraderRules = { ...base, [flag]: next };
        if (next && OPT_OUT_FLAGS.has(flag)) delete sent[flag];
        return sent;
      },
      { flag, next },
    );

  // Oscar overtake/blocker access (traderRules.oscarEnabled, fail-closed).
  const toggleOscarEnabled = (id: string, next: boolean) => setRuleFlag(id, 'oscarEnabled', next);

  // TapeLab level. Same whole-object PATCH as the other capability flags;
  // "off" is spelled by dropping the key rather than writing null, because the
  // relay validator only accepts the two level strings and an absent key is
  // exactly what resolves to no access.
  const setLabAccess = (id: string, next: 'off' | 'viewer' | 'researcher') =>
    writeRules(id, `set TapeLab to ${next}`, 'The previous level stands.', (base) => {
      const { labAccess: _prev, ...rest } = base;
      void _prev;
      return next === 'off' ? rest : { ...rest, labAccess: next };
    });

  // Armed one-click access is deliberately independent of ordinary ticket,
  // bot, and Oscar access. The relay fails closed on a missing key.
  const toggleOneClickEnabled = (id: string, next: boolean) => setRuleFlag(id, 'oneClickEnabled', next);

  // Live Trading V2 surface access (traderRules.lt2Enabled, fail-closed). The
  // per-trader kill switch the controlled-release runbook's rollback step names
  // (docs/live-trading-v2-controlled-release.md): turning it off refuses new
  // LT2-tagged requests at the relay before credential resolution, and does NOT
  // touch orders the venue has already accepted.
  //
  // Grants nothing else. `oneClickEnabled` in particular is left exactly as it
  // was — LT2 needs it too, but the card reports the shortfall (· inactive)
  // rather than silently widening access.
  const toggleLt2Enabled = (id: string, next: boolean) => setRuleFlag(id, 'lt2Enabled', next);

  // StreamRift Cockpit / Larry bot-management access (traderRules.cockpitEnabled,
  // fail-closed). Same whole-object PATCH merge as the Oscar toggle; grants
  // nothing else.
  const toggleCockpitEnabled = (id: string, next: boolean) => setRuleFlag(id, 'cockpitEnabled', next);

  // RFQ desk access (traderRules.rfqEnabled, fail-closed). Same whole-object
  // PATCH merge as the Oscar/cockpit toggles; grants nothing else.
  const toggleRfqEnabled = (id: string, next: boolean) => setRuleFlag(id, 'rfqEnabled', next);

  // Single-game live trading view access (traderRules.liveGameEnabled,
  // fail-closed). Same whole-object PATCH merge as the Oscar/cockpit/RFQ
  // toggles; grants the /live/:gameKey surface only — order capability stays
  // with the one-click/LT2 chips.
  const toggleLiveGameEnabled = (id: string, next: boolean) => setRuleFlag(id, 'liveGameEnabled', next);

  // Sniper access (traderRules.sniperEnabled, fail-closed, NO manager role
  // pass — see canAccessSniper). Same whole-object PATCH merge as the other
  // capability toggles. Grants the per-game sniper screen + /api/sniper;
  // the runner executes on the ORG's keys, so grant deliberately.
  const toggleSniperEnabled = (id: string, next: boolean) => setRuleFlag(id, 'sniperEnabled', next);

  // Penelope's three OPT-OUT locks (2026-10-08): penelopeEnabled, penelopeLiveEnabled, penelopeAdvancedEnabled.
  // Everyone has Penelope, live and advanced unless the key is the literal false — so "on" is stored as ABSENT
  // (the key is deleted), never as true, and "off" is stored as false. Same whole-object PATCH merge as the other
  // capability toggles; the relay resolves the three the same way (services/penelope/access.ts).
  // Opt-out locks: "on" drops the key (setRuleFlag knows), "off" writes false.
  const togglePenelopeEnabled = (id: string, next: boolean) => setRuleFlag(id, 'penelopeEnabled', next);
  const togglePenelopeLiveEnabled = (id: string, next: boolean) => setRuleFlag(id, 'penelopeLiveEnabled', next);
  const togglePenelopeAdvancedEnabled = (id: string, next: boolean) => setRuleFlag(id, 'penelopeAdvancedEnabled', next);

  // Live Trading V3 Attack (W/E) access (traderRules.attackEnabled,
  // fail-closed). Grants nothing else. Missing sizing uses the shared
  // conservative default; configured ceilings survive this toggle, including
  // disable/re-enable.
  const toggleAttackEnabled = (id: string, next: boolean) => setRuleFlag(id, 'attackEnabled', next);

  // Low bandwidth mode (users.prefs.lowBandwidth, 2026-10-19) — a PREFERENCE,
  // not a grant: the trader can flip it themselves (Settings, the status-bar
  // chip); this is how a manager sets it for someone on a thin link. Not a
  // traderRules write, so it skips the rules queue: the relay merges the one
  // key into the stored prefs, and only the card's prefs take the reply (the
  // rules maps above stay the rules chips' own). Reaches the trader's open
  // screens at their next profile read (reload or sign-in).
  const setLowBandwidth = async (id: string, next: boolean): Promise<void> => {
    setError(null);
    const username = members.find((m) => m.id === id)?.username ?? 'this member';
    const what = `turn low bandwidth mode ${next ? 'on' : 'off'} for ${username}`;
    const still = 'Reload to check the saved setting before trying again.';
    let res: Response;
    try {
      res = await apiFetch(`/api/manager/traders/${id}${orgQueryParam}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prefs: { lowBandwidth: next } }),
      });
    } catch {
      setError(`Couldn't ${what} (network error). ${still}`);
      return;
    }
    const data = await replyBody(res);
    if (!res.ok) {
      const reason = typeof data.error === 'string' && data.error ? `: ${data.error}` : '';
      setError(`Couldn't ${what} (HTTP ${res.status}${reason}). ${still}`);
      return;
    }
    const user = data.user && typeof data.user === 'object' ? data.user : {};
    if (!('prefs' in user)) {
      setError(`Couldn't confirm the setting for ${username}. ${still}`);
      return;
    }
    setMembers((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const prefs = parseTraderPrefs(user.prefs);
      return { ...m, prefs };
    }));
  };

  const applyMemberPatch = (
    next: Pick<OrgMember, 'id' | 'username' | 'maxContracts' | 'maxPositions' | 'traderRules'>,
  ) => {
    // An Edit limits save is a confirmed rules reply too: the next chip write
    // builds on it, and a capability it re-sent ON is called out.
    confirmedRules.current.set(next.id, next.traderRules ?? null);
    setMembers((prev) => prev.map((m) => (m.id === next.id ? { ...m, ...next } : m)));
    callOutResent(next.id, next.username, next.traderRules ?? null);
  };

  const clearGeneratedPassword = () => setGeneratedPassword(null);

  return {
    members,
    usageByUserId,
    awaitingOrg,
    loading,
    error,
    setError,
    generatedPassword,
    clearGeneratedPassword,
    submitting,
    managerSubmitting,
    orgBotsEnabled,
    setOrgBotsEnabled,
    membersLoadError,
    reloadMembers: fetchMembers,
    canCreateManager,
    canCreateTrader,
    canGovern,
    createTrader,
    createManager,
    renameMember,
    deleteMember,
    resetPassword,
    toggleUseOrgKeys,
    toggleBotsEnabled,
    toggleOscarEnabled,
    toggleOneClickEnabled,
    toggleLt2Enabled,
    toggleCockpitEnabled,
    toggleRfqEnabled,
    setLabAccess,
    toggleLiveGameEnabled,
    toggleSniperEnabled,
    togglePenelopeEnabled,
    togglePenelopeLiveEnabled,
    togglePenelopeAdvancedEnabled,
    toggleAttackEnabled,
    setLowBandwidth,
    applyMemberPatch,
  };
}
